import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../ui/Modal';

// Shared by the Team Leader view and the Entry Control page — the donning/doffing gates
// and their checklists must read the same wherever a responder is suited up or screened.

export const DEFAULT_PPE_DURATION_MINUTES = 30;
export const PPE_WARNING_MINUTES = 5;

// Mirrors constants/donningChecklist.js on the server — every item must be checked
// before a member can be sent into a Hot/Warm zone. Enforced server-side too, not just
// disabled here.
export const DONNING_CHECKLIST_ITEMS = [
  { key: 'attendanceConfirmed', label: 'Attendance confirmed at incident / assembly area' },
  { key: 'medicalMonitoringDone', label: 'Pre-entry medical monitoring completed' },
  { key: 'hydrationBathroomConfirmed', label: 'Hydration / bathroom confirmed' },
  { key: 'belongingsSharpsRemoved', label: 'Personal belongings and sharp objects removed' },
  { key: 'ppeSizeInspected', label: 'Correct PPE size selected and garment inspected' },
  { key: 'paprFilterBatteryChecked', label: 'PAPR / filter / battery check completed where applicable' },
  { key: 'tldIssuedInitialRecorded', label: 'TLD / dosimeter issued and initial reading recorded' },
  { key: 'buddyCheckCompleted', label: 'Buddy closure check completed by observer' },
];

// Mirrors constants/doffingChecklist.js on the server — every item (plus a final
// status) must be complete before a member can re-enter a Hot/Warm zone. Most
// contamination incidents happen on the way out, not in, so this is enforced with the
// same rigor as donning.
export const DOFFING_CHECKLIST_ITEMS = [
  { key: 'movedToDoffingArea', label: 'Moved to designated doffing / transition area' },
  { key: 'buddyAssistedRemoval', label: 'Buddy-assisted removal confirmed — not self-doffed alone' },
  { key: 'ppeRemovedInSequence', label: 'PPE removed in correct sequence' },
  { key: 'followUpReadingRecorded', label: 'Follow-up contamination/radiation reading recorded' },
  { key: 'finalReadingRecorded', label: 'Final contamination/radiation reading recorded' },
  { key: 'postExitMedicalMonitoring', label: 'Post-exit medical monitoring completed' },
];

export const FINAL_STATUS_OPTIONS = [
  'Clean / Cleared',
  'Needs Repeat Screen',
  'Staff Exposure Concern',
  'Send to Medical Check',
];

export function ppeRemainingMinutes(ppeDonnedAt, nowMs, durationMinutes) {
  if (!ppeDonnedAt) return null;
  const elapsedMin = (nowMs - new Date(ppeDonnedAt).getTime()) / 60000;
  return Math.max(0, Math.ceil((durationMinutes || DEFAULT_PPE_DURATION_MINUTES) - elapsedMin));
}

export function isChecklistComplete(checklist, items = DONNING_CHECKLIST_ITEMS) {
  return items.every((item) => !!checklist?.[item.key]);
}

export function checklistDoneCount(checklist, items = DONNING_CHECKLIST_ITEMS) {
  return items.filter((item) => checklist?.[item.key]).length;
}

// A member who exited a zone but never closed out their doffing screening from that
// rotation can't be sent back in.
export function hasPendingDoffing(m) {
  if (!m.exitedZoneAt) return false;
  return !m.doffingCompletedAt || new Date(m.exitedZoneAt) > new Date(m.doffingCompletedAt);
}

// Mirrors the server's zone-entry gate (AnnouncementMemberController.validateZoneEntry),
// so the button reflects reality instead of failing on click. Returns the reason entry
// isn't allowed yet, or null when it is.
export function zoneEntryBlocker(m) {
  if (!m.activeSince) return 'Not marked as arrived yet.';
  if (!m.zoneName || m.zoneName === 'Cold Zone') return 'Needs a Hot or Warm zone assignment.';
  if (hasPendingDoffing(m)) return 'Doffing screening from the last rotation is still open.';
  if (!isChecklistComplete(m.donningChecklist)) return 'Donning checklist is incomplete.';
  return null;
}

function ChecklistRows({ items, value, onChange }) {
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <label
          key={item.key}
          className="flex items-center justify-between gap-3 bg-neutral-50 border border-neutral-200 rounded-lg px-3.5 py-2.5 cursor-pointer dark:bg-ink-800 dark:border-neutral-700"
        >
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">{i + 1}. {item.label}</span>
          <input
            type="checkbox"
            checked={!!value[item.key]}
            onChange={(e) => onChange({ ...value, [item.key]: e.target.checked })}
            className="w-5 h-5 rounded border-neutral-300 text-seha-500 focus:ring-seha-500 shrink-0 dark:border-neutral-600"
          />
        </label>
      ))}
    </div>
  );
}

// `member` is the roster row being suited up (null = closed); onSave gets the API patch.
// Keyed on the member so the form state is initialized from that row on its very first
// render — filling it in from an effect instead flashes an empty form for a frame, and a
// tap landing in that frame gets overwritten.
export function DonningModal({ member, onClose, onSave }) {
  return <DonningForm key={member?.id ?? 'closed'} member={member} onClose={onClose} onSave={onSave} />;
}

function DonningForm({ member, onClose, onSave }) {
  const [checklist, setChecklist] = useState(member?.donningChecklist || {});
  const [ppeNumber, setPpeNumber] = useState(member?.ppeNumber || '');
  const [tldNumber, setTldNumber] = useState(member?.tldNumber || '');
  const [reading, setReading] = useState(member?.radiationInitialReading || '');

  return (
    <Modal
      open={!!member}
      onClose={onClose}
      title="Pre-Entry Donning Check"
      subtitle={member ? `${member.name} — every item is required before Hot/Warm zone entry.` : ''}
      size="lg"
      confirmLabel="Save"
      onConfirm={() => onSave({ donningChecklist: checklist, ppeNumber, tldNumber, radiationInitialReading: reading })}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="form-label" htmlFor="ppe-number">PPE Number</label>
            <input id="ppe-number" className="form-input" value={ppeNumber} onChange={(e) => setPpeNumber(e.target.value)} />
          </div>
          <div>
            <label className="form-label" htmlFor="tld-number">TLD / Dosimeter No.</label>
            <input id="tld-number" className="form-input" value={tldNumber} onChange={(e) => setTldNumber(e.target.value)} />
          </div>
          <div>
            <label className="form-label" htmlFor="initial-reading">Initial Reading</label>
            <input id="initial-reading" className="form-input" value={reading} onChange={(e) => setReading(e.target.value)} placeholder="0.00" />
          </div>
        </div>
        <ChecklistRows items={DONNING_CHECKLIST_ITEMS} value={checklist} onChange={setChecklist} />
      </div>
    </Modal>
  );
}

// Closing out a rotation stamps doffingCompletedAt, which is what lifts the re-entry block.
// Keyed per member for the same reason as DonningModal.
export function DoffingModal({ member, onClose, onSave }) {
  return <DoffingForm key={member?.id ?? 'closed'} member={member} onClose={onClose} onSave={onSave} />;
}

function DoffingForm({ member, onClose, onSave }) {
  const [checklist, setChecklist] = useState(member?.doffingChecklist || {});
  const [followUpReading, setFollowUpReading] = useState(member?.followUpReading || '');
  const [finalReading, setFinalReading] = useState(member?.finalReading || '');
  const [finalStatus, setFinalStatus] = useState(member?.finalStatus || '');

  const save = () => {
    if (!isChecklistComplete(checklist, DOFFING_CHECKLIST_ITEMS)) {
      toast.error('Complete every item before closing out this rotation.');
      return;
    }
    if (!finalStatus) {
      toast.error('Select a final status before closing out this rotation.');
      return;
    }
    // The next rotation starts with a fresh pre-entry check (PPE/TLD numbers stay as
    // editable defaults) — completing one rotation's donning doesn't clear the next entry.
    onSave({
      doffingChecklist: checklist, followUpReading, finalReading, finalStatus,
      doffingCompletedAt: new Date().toISOString(), donningChecklist: {},
    });
  };

  return (
    <Modal
      open={!!member}
      onClose={onClose}
      title="Post-Exit Doffing Screening"
      subtitle={member ? `${member.name} — must be closed out before re-entry is allowed. Safe to close this and come back to it.` : ''}
      size="lg"
      confirmLabel="Close Out Rotation"
      onConfirm={save}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="form-label" htmlFor="followup-reading">Follow-up Reading</label>
            <input id="followup-reading" className="form-input" value={followUpReading} onChange={(e) => setFollowUpReading(e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <label className="form-label" htmlFor="final-reading">Final Reading</label>
            <input id="final-reading" className="form-input" value={finalReading} onChange={(e) => setFinalReading(e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <label className="form-label" htmlFor="final-status">Final Status</label>
            <select id="final-status" className="form-input" value={finalStatus} onChange={(e) => setFinalStatus(e.target.value)}>
              <option value="">Select status…</option>
              {FINAL_STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>
        <ChecklistRows items={DOFFING_CHECKLIST_ITEMS} value={checklist} onChange={setChecklist} />
        {finalStatus && finalStatus !== 'Clean / Cleared' && (
          <div className="alert-row warning">
            This status flags the member for follow-up — command should be made aware before they're reassigned.
          </div>
        )}
      </div>
    </Modal>
  );
}
